import React, { useState, useRef } from 'react';
import { grammarService } from '../../../services/grammarService';

interface GrammarImportProps {
  onError: (error: string | null) => void;
  onSuccess?: () => void;
}

// ImportData is now just an array of features
type ImportData = Array<{
  feature_id: string;
  grammar_feature: string;
  category: string;
  sub_category: string;
  tags: string[];
  example_1: string;
  example_2: string;
  important_footnotes: string;
  cefr_introduction: string;
  cefr_mastery: string;
  complexity_score: number;
  common_confusions: Array<{
    feature_id: string;
    explanation: string;
  }>;
  formality: string;
  register: string[];
  structural_formula: string;
  business_contexts: Array<{
    context: string;
    example: string;
    frequency: string;
  }>;
  distractor_patterns: string[];
  prerequisites: string[];
  enables: string[];
  combines_well_with: string[];
  grammar_feature_full_path: string;
  index_number: number;
}>;

interface ImportResult {
  categories: number;
  features: number;
  relationships: number;
  errors: string[];
}

interface SchemaField {
  name: string;
  type: string;
  required: boolean;
  description: string;
  example?: any;
  enumValues?: string[];
  defaultValue?: any;
}

interface SchemaSection {
  name: string;
  description: string;
  fields: SchemaField[];
}

const SCHEMA_DOCUMENTATION: SchemaSection[] = [
  {
    name: "Grammar Features Array",
    description: "Import an array of grammar features, each with comprehensive linguistic data",
    fields: [
      {
        name: "feature_id",
        type: "string", 
        required: true,
        description: "Unique identifier for the feature (7-character alphanumeric)",
        example: "TEST-001"
      },
      {
        name: "grammar_feature",
        type: "string",
        required: true, 
        description: "Display name/title of the grammar feature",
        example: "Should for advice"
      },
      {
        name: "category",
        type: "string",
        required: true,
        description: "Category this feature belongs to (must exist in categories)",
        example: "modals"
      },
      {
        name: "sub_category",
        type: "string",
        required: true,
        description: "More specific grammatical sub-category",
        example: "type_3_conditional"
      },
      {
        name: "cefr_introduction", 
        type: "string",
        required: true,
        description: "CEFR level where the feature is typically introduced",
        example: "B2",
        enumValues: ["A1", "A2", "B1", "B2", "C1", "C2"]
      },
      {
        name: "cefr_mastery",
        type: "string", 
        required: true,
        description: "CEFR level where mastery of the feature is expected",
        example: "C1",
        enumValues: ["A1", "A2", "B1", "B2", "C1", "C2"]
      },
      {
        name: "complexity_score",
        type: "integer",
        required: true,
        description: "A numerical score indicating the complexity of the grammar feature",
        example: 8
      },
      {
        name: "example_1",
        type: "string",
        required: true,
        description: "First example sentence demonstrating the grammar feature",
        example: "If he had studied, he would have passed."
      },
      {
        name: "example_2", 
        type: "string",
        required: true,
        description: "Second example sentence demonstrating the grammar feature", 
        example: "She would have helped if she had known you needed it."
      },
      {
        name: "important_footnotes",
        type: "string",
        required: true,
        description: "Important notes, nuances, or exceptions for the feature's usage",
        example: "Used to describe a hypothetical situation in the past and its hypothetical past result"
      },
      {
        name: "structural_formula",
        type: "string",
        required: true,
        description: "Syntactic formula or structure of the grammar feature",
        example: "If + Subject + had + [Past Participle], Subject + would have + [Past Participle]"
      },
      {
        name: "formality",
        type: "string",
        required: true,
        description: "General formality level (e.g., 'neutral', 'formal', 'informal')",
        example: "neutral"
      },
      {
        name: "register",
        type: "array",
        required: true,
        description: "Contexts or domains where the feature is typically used",
        example: ["spoken", "written", "academic"],
        enumValues: ["spoken", "written", "academic", "business", "casual"]
      },
      {
        name: "tags",
        type: "array",
        required: true,
        description: "Keywords or descriptive tags for the feature",
        example: ["conditionals", "hypothetical", "past", "unreal"]
      },
      {
        name: "business_contexts",
        type: "array",
        required: true,
        description: "Examples of how the feature is used in business scenarios",
        example: [{
          "context": "post-mortem analysis",
          "example": "If we had allocated more resources, the project would have been completed on time.",
          "frequency": "common"
        }]
      },
      {
        name: "common_confusions",
        type: "array",
        required: true,
        description: "List of common confusions associated with this feature",
        example: [{
          "feature_id": "I7I6WZM-B",
          "explanation": "Often confused with Mixed Conditionals. The 3rd conditional refers entirely to the past."
        }]
      },
      {
        name: "distractor_patterns",
        type: "array",
        required: true,
        description: "Patterns of common errors or 'distractors' used in assessments (array of strings)",
        example: []
      },
      {
        name: "prerequisites",
        type: "array", 
        required: true,
        description: "IDs of grammar features that are typically prerequisites for understanding this one",
        example: ["TENS008", "MODL004"]
      },
      {
        name: "enables",
        type: "array",
        required: true,
        description: "IDs of grammar features that are enabled or built upon by this one",
        example: ["COND-ADV-001", "COND-MIX-001"]
      },
      {
        name: "combines_well_with",
        type: "array",
        required: true,
        description: "IDs of other grammar features that often appear with or complement this one",
        example: ["PERF-002", "MODL-PAST-001"]
      },
      {
        name: "grammar_feature_full_path",
        type: "string",
        required: true,
        description: "Full hierarchical path of the grammar feature for detailed classification",
        example: "Conditional -> Type 3 -> Statement -> Positive"
      },
      {
        name: "index_number",
        type: "integer",
        required: true,
        description: "Sequential index number of the feature",
        example: 2373
      }
    ]
  }
];

const EXAMPLE_TEMPLATE = `[
  {
    "feature_id": "99IM8EM-B",
    "grammar_feature": "Conditional 3 -> Statement -> Positive",
    "category": "conditionals",
    "sub_category": "type_3_conditional",
    "tags": ["conditionals", "hypothetical", "past", "unreal"],
    "example_1": "If he had studied, he would have passed.",
    "example_2": "She would have helped if she had known you needed it.",
    "important_footnotes": "Used to describe a hypothetical situation in the past and its hypothetical past result (something that did not happen).",
    "cefr_introduction": "B2",
    "cefr_mastery": "C1",
    "complexity_score": 8,
    "common_confusions": [
      {
        "feature_id": "I7I6WZM-B",
        "explanation": "Often confused with Mixed Conditionals. The 3rd conditional refers entirely to the past (past condition, past result), while mixed conditionals link different time frames (e.g., past condition, present result)."
      }
    ],
    "formality": "neutral",
    "register": ["spoken", "written", "academic"],
    "structural_formula": "If + Subject + had + [Past Participle], Subject + would have + [Past Participle]",
    "business_contexts": [
      {
        "context": "post-mortem analysis",
        "example": "If we had allocated more resources, the project would have been completed on time.",
        "frequency": "common"
      },
      {
        "context": "regret/missed opportunities",
        "example": "If I had accepted that offer, I would have gained valuable experience.",
        "frequency": "common"
      }
    ],
    "distractor_patterns": [],
    "prerequisites": ["TENS008", "MODL004"],
    "enables": ["COND-ADV-001", "COND-MIX-001"],
    "combines_well_with": ["PERF-002", "MODL-PAST-001"],
    "grammar_feature_full_path": "Conditional -> Type 3 -> Statement -> Positive",
    "index_number": 2373
  }
]`;

const JSON_SCHEMA = {
  "$schema": "http://json-schema.org/draft-07/schema#",
  "title": "Grammar Feature Import",
  "description": "Import an array of grammar features",
  "type": "array",
  "items": {
    "type": "object",
    "title": "Grammar Feature",
    "description": "Schema for a single grammar feature, designed for comprehensive linguistic data and RAG applications.",
    "properties": {
      "feature_id": {
        "type": "string",
        "description": "Unique identifier for the grammar feature."
      },
      "grammar_feature": {
        "type": "string",
        "description": "Human-readable name of the grammar feature."
      },
      "category": {
        "type": "string",
        "description": "Broad grammatical category (e.g., 'verb_tenses', 'modals', 'articles')."
      },
      "sub_category": {
        "type": "string",
        "description": "More specific grammatical sub-category."
      },
      "tags": {
        "type": "array",
        "description": "Keywords or descriptive tags for the feature.",
        "items": {
          "type": "string"
        }
      },
      "example_1": {
        "type": "string",
        "description": "First example sentence demonstrating the grammar feature."
      },
      "example_2": {
        "type": "string",
        "description": "Second example sentence demonstrating the grammar feature."
      },
      "important_footnotes": {
        "type": "string",
        "description": "Important notes, nuances, or exceptions for the feature's usage."
      },
      "cefr_introduction": {
        "type": "string",
        "description": "CEFR level where the feature is typically introduced."
      },
      "cefr_mastery": {
        "type": "string",
        "description": "CEFR level where mastery of the feature is expected."
      },
      "complexity_score": {
        "type": "integer",
        "description": "A numerical score indicating the complexity of the grammar feature."
      },
      "common_confusions": {
        "type": "array",
        "description": "List of common confusions associated with this feature.",
        "items": {
          "type": "object",
          "properties": {
            "feature_id": {
              "type": "string",
              "description": "ID of a commonly confused feature, if applicable. Can be 'N/A' if not a specific feature ID."
            },
            "explanation": {
              "type": "string",
              "description": "Explanation of the confusion and how to differentiate."
            }
          },
          "required": [
            "feature_id",
            "explanation"
          ]
        }
      },
      "formality": {
        "type": "string",
        "description": "General formality level (e.g., 'neutral', 'formal', 'informal')."
      },
      "register": {
        "type": "array",
        "description": "Contexts or domains where the feature is typically used (e.g., 'spoken', 'written', 'business').",
        "items": {
          "type": "string"
        }
      },
      "structural_formula": {
        "type": "string",
        "description": "Syntactic formula or structure of the grammar feature."
      },
      "business_contexts": {
        "type": "array",
        "description": "Examples of how the feature is used in business scenarios.",
        "items": {
          "type": "object",
          "properties": {
            "context": {
              "type": "string",
              "description": "Specific business context."
            },
            "example": {
              "type": "string",
              "description": "Example sentence in that business context."
            },
            "frequency": {
              "type": "string",
              "description": "How common the usage is in this context ('common', 'less_common', 'rare')."
            }
          },
          "required": [
            "context",
            "example",
            "frequency"
          ]
        }
      },
      "distractor_patterns": {
        "type": "array",
        "description": "Patterns of common errors or 'distractors' used in assessments. (Currently empty in provided data)",
        "items": {
          "type": "string"
        }
      },
      "prerequisites": {
        "type": "array",
        "description": "IDs of grammar features that are typically prerequisites for understanding this one.",
        "items": {
          "type": "string"
        }
      },
      "enables": {
        "type": "array",
        "description": "IDs of grammar features that are enabled or built upon by this one.",
        "items": {
          "type": "string"
        }
      },
      "combines_well_with": {
        "type": "array",
        "description": "IDs of other grammar features that often appear with or complement this one.",
        "items": {
          "type": "string"
        }
      },
      "grammar_feature_full_path": {
        "type": "string",
        "description": "Full hierarchical path of the grammar feature for detailed classification."
      },
      "index_number": {
        "type": "integer",
        "description": "Sequential index number of the feature."
      }
    },
    "required": [
      "feature_id",
      "grammar_feature",
      "category",
      "sub_category",
      "tags",
      "example_1",
      "example_2",
      "important_footnotes",
      "cefr_introduction",
      "cefr_mastery",
      "complexity_score",
      "common_confusions",
      "formality",
      "register",
      "structural_formula",
      "business_contexts",
      "distractor_patterns",
      "prerequisites",
      "enables",
      "combines_well_with",
      "grammar_feature_full_path",
      "index_number"
    ]
  }
};

const VALIDATION_RULES = [
  {
    rule: "JSON Structure",
    description: "Must be an array of grammar feature objects"
  },
  {
    rule: "Required Fields",
    description: "ALL fields are required: feature_id, grammar_feature, category, sub_category, tags, example_1, example_2, important_footnotes, cefr_introduction, cefr_mastery, complexity_score, common_confusions, formality, register, structural_formula, business_contexts, distractor_patterns, prerequisites, enables, combines_well_with, grammar_feature_full_path, index_number"
  },
  {
    rule: "Unique Identifiers",
    description: "All feature_ids must be unique within the import batch"
  },
  {
    rule: "CEFR Levels",
    description: "Must be one of: A1, A2, B1, B2, C1, C2 (case sensitive)"
  },
  {
    rule: "Complexity Score",
    description: "Must be an integer (no decimals)"
  },
  {
    rule: "Array Fields",
    description: "Empty arrays are acceptable but must be present: tags, register, business_contexts, common_confusions, distractor_patterns, prerequisites, enables, combines_well_with"
  },
  {
    rule: "Business Context Frequency",
    description: "Each business context frequency must be one of: 'common', 'less_common', or 'rare'"
  }
];

const GrammarImport: React.FC<GrammarImportProps> = ({ onError, onSuccess }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [isValidJson, setIsValidJson] = useState(true);
  const [parsedData, setParsedData] = useState<ImportData | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [docTab, setDocTab] = useState<'schema' | 'json-schema' | 'example' | 'validation'>('schema');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateAndParseJson = (jsonString: string) => {
    if (!jsonString.trim()) {
      setIsValidJson(true);
      setParsedData(null);
      return;
    }

    try {
      const parsed = JSON.parse(jsonString);
      setIsValidJson(true);
      setParsedData(parsed);
      onError(null);
    } catch (error) {
      setIsValidJson(false);
      setParsedData(null);
      onError('Invalid JSON format');
    }
  };

  const handleJsonChange = (value: string) => {
    setJsonInput(value);
    validateAndParseJson(value);
    setImportResult(null);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      onError('Please select a JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      handleJsonChange(content);
    };
    reader.onerror = () => {
      onError('Failed to read file');
    };
    reader.readAsText(file);
  };

  const formatJson = () => {
    if (parsedData) {
      const formatted = JSON.stringify(parsedData, null, 2);
      setJsonInput(formatted);
    }
  };

  const loadExample = () => {
    setJsonInput(EXAMPLE_TEMPLATE);
    validateAndParseJson(EXAMPLE_TEMPLATE);
    setShowDocumentation(false); // Hide documentation when loading example
  };

  const handleImport = async () => {
    if (!parsedData || !isValidJson || !Array.isArray(parsedData)) {
      onError('Please provide valid JSON data (array of features)');
      return;
    }

    try {
      setImporting(true);
      onError(null);
      
      // Wrap the array in the format expected by the backend
      const importPayload = {
        categories: [],
        features: parsedData,
        relationships: []
      };
      
      const result = await grammarService.importFeatures(importPayload as any);
      setImportResult(result);
      
      if (result.errors.length === 0) {
        onSuccess?.();
      }
    } catch (error) {
      console.error('Import error:', error);
      onError(error instanceof Error ? error.message : 'Failed to import data');
    } finally {
      setImporting(false);
    }
  };

  const clearData = () => {
    setJsonInput('');
    setParsedData(null);
    setIsValidJson(true);
    setImportResult(null);
    onError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionName)) {
        newSet.delete(sectionName);
      } else {
        newSet.add(sectionName);
      }
      return newSet;
    });
  };

  const renderDocumentation = () => (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-gray-800">Schema Documentation</h3>
        <button
          onClick={() => setShowDocumentation(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="flex space-x-2 mb-4">
        <button
          onClick={() => setDocTab('schema')}
          className={`px-3 py-1 text-sm rounded ${
            docTab === 'schema' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📚 Schema
        </button>
        <button
          onClick={() => setDocTab('json-schema')}
          className={`px-3 py-1 text-sm rounded ${
            docTab === 'json-schema' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          🔗 JSON Schema
        </button>
        <button
          onClick={() => setDocTab('example')}
          className={`px-3 py-1 text-sm rounded ${
            docTab === 'example' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 Example
        </button>
        <button
          onClick={() => setDocTab('validation')}
          className={`px-3 py-1 text-sm rounded ${
            docTab === 'validation' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          🔧 Validation
        </button>
      </div>

      {docTab === 'schema' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 mb-4">
            Interactive schema documentation with field details and examples. Click sections to expand.
          </p>
          {SCHEMA_DOCUMENTATION.map(section => (
            <div key={section.name} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(section.name)}
                className="flex justify-between items-center w-full text-left p-3 bg-white hover:bg-blue-50 transition-colors"
              >
                <div>
                  <span className="font-medium text-blue-800">{section.name}</span>
                  <p className="text-sm text-gray-600 mt-1">{section.description}</p>
                </div>
                <svg 
                  className={`w-5 h-5 text-blue-600 transition-transform ${
                    expandedSections.has(section.name) ? 'transform rotate-180' : ''
                  }`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {expandedSections.has(section.name) && (
                <div className="border-t border-gray-200 bg-gray-50 p-4">
                  <div className="space-y-4">
                    {section.fields.map(field => (
                      <div key={field.name} className="bg-white rounded border border-gray-200 p-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <code className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                              {field.name}
                            </code>
                            <span className="text-sm text-gray-500">({field.type})</span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              field.required 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {field.required ? 'Required' : 'Optional'}
                            </span>
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-2">{field.description}</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {field.example !== undefined && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Example</span>
                              <code className="block text-xs bg-gray-100 p-2 rounded mt-1 text-gray-800">
                                {JSON.stringify(field.example)}
                              </code>
                            </div>
                          )}
                          
                          {field.enumValues && field.enumValues.length > 0 && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Allowed Values</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {field.enumValues.map(value => (
                                  <span key={value} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                    {value}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {field.defaultValue !== undefined && (
                            <div>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Default</span>
                              <code className="block text-xs bg-gray-100 p-2 rounded mt-1 text-gray-800">
                                {JSON.stringify(field.defaultValue)}
                              </code>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {docTab === 'json-schema' && (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Complete JSON Schema definition for grammar feature imports. Use this for validation or generating TypeScript types.
          </p>
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-h-96 overflow-y-auto">
            <pre className="text-sm text-green-400 whitespace-pre">
              {JSON.stringify(JSON_SCHEMA, null, 2)}
            </pre>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => navigator.clipboard.writeText(JSON.stringify(JSON_SCHEMA, null, 2))}
              className="px-3 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              📋 Copy Schema
            </button>
          </div>
        </div>
      )}

      {docTab === 'example' && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-gray-600">Complete working example you can copy and modify</p>
            <button
              onClick={loadExample}
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              📋 Use This Example
            </button>
          </div>
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm text-green-400 whitespace-pre-wrap">
              {EXAMPLE_TEMPLATE}
            </pre>
          </div>
        </div>
      )}

      {docTab === 'validation' && (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Import validation rules that your JSON data must follow to be accepted.
          </p>
          <div className="space-y-3">
            {VALIDATION_RULES.map((rule, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-green-600 text-sm font-bold">{index + 1}</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-800">{rule.rule}</h4>
                    <p className="text-sm text-gray-600 mt-1">{rule.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderPreview = () => {
    if (!parsedData || !Array.isArray(parsedData)) return null;

    const featureCount = parsedData.length;

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-3">Import Preview</h3>
        <div className="text-center mb-4">
          <div className="text-3xl font-bold text-blue-600">{featureCount}</div>
          <div className="text-blue-700">Grammar Features</div>
        </div>

        {featureCount > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Features to import:</h4>
            <div className="text-xs text-blue-700 space-y-1">
              {parsedData.slice(0, 3).map((feature, index) => (
                <div key={index} className="truncate">
                  <span className="font-mono">{feature.feature_id}</span>: {feature.grammar_feature}
                </div>
              ))}
              {featureCount > 3 && (
                <div className="text-blue-600">... and {featureCount - 3} more</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderResult = () => {
    if (!importResult) return null;

    const hasErrors = importResult.errors.length > 0;
    const totalImported = importResult.categories + importResult.features + importResult.relationships;

    return (
      <div className={`border rounded-lg p-4 ${hasErrors ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
        <h3 className={`font-medium mb-3 ${hasErrors ? 'text-yellow-900' : 'text-green-900'}`}>
          Import Results
        </h3>
        
        <div className="grid grid-cols-3 gap-4 text-sm mb-4">
          <div className="text-center">
            <div className={`text-2xl font-bold ${hasErrors ? 'text-yellow-600' : 'text-green-600'}`}>
              {importResult.categories}
            </div>
            <div className={hasErrors ? 'text-yellow-700' : 'text-green-700'}>Categories</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${hasErrors ? 'text-yellow-600' : 'text-green-600'}`}>
              {importResult.features}
            </div>
            <div className={hasErrors ? 'text-yellow-700' : 'text-green-700'}>Features</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${hasErrors ? 'text-yellow-600' : 'text-green-600'}`}>
              {importResult.relationships}
            </div>
            <div className={hasErrors ? 'text-yellow-700' : 'text-green-700'}>Relationships</div>
          </div>
        </div>

        {totalImported > 0 && (
          <div className={`text-sm ${hasErrors ? 'text-yellow-700' : 'text-green-700'} mb-3`}>
            ✅ Successfully imported {totalImported} items
          </div>
        )}

        {hasErrors && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-yellow-800">Errors:</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              {importResult.errors.map((error, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>{error}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Import Grammar Features</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowDocumentation(!showDocumentation)}
            className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            {showDocumentation ? 'Hide Documentation' : 'Show Documentation'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
          >
            Upload JSON File
          </button>
        </div>
      </div>

      {showDocumentation && renderDocumentation()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* JSON Input */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">
              JSON Data
            </label>
            <div className="flex space-x-2">
              {parsedData && (
                <button
                  onClick={formatJson}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Format JSON
                </button>
              )}
              <button
                onClick={clearData}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Clear
              </button>
            </div>
          </div>
          
          <textarea
            value={jsonInput}
            onChange={(e) => handleJsonChange(e.target.value)}
            rows={20}
            className={`w-full px-3 py-2 border rounded-md shadow-sm font-mono text-sm ${
              !isValidJson 
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
            }`}
            placeholder="Paste your JSON data here or upload a file..."
          />
          
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {jsonInput.length > 0 && (
                <span className={isValidJson ? 'text-green-600' : 'text-red-600'}>
                  {isValidJson ? '✓ Valid JSON' : '✗ Invalid JSON'}
                </span>
              )}
            </div>
            
            <button
              onClick={handleImport}
              disabled={!parsedData || !isValidJson || importing}
              className={`px-4 py-2 rounded font-medium ${
                parsedData && isValidJson && !importing
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {importing ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  <span>Importing...</span>
                </div>
              ) : (
                'Import Data'
              )}
            </button>
          </div>
        </div>

        {/* Preview and Results */}
        <div className="space-y-4">
          {renderPreview()}
          {renderResult()}
          
          {!parsedData && !importResult && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <div className="text-gray-400 mb-3">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-600 text-sm">
                Paste JSON data or upload a file to see the import preview
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Import Guidelines</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• JSON must be an array of grammar feature objects</li>
          <li>• ALL fields are required for each feature (see schema for complete list)</li>
          <li>• Each feature must have a unique feature_id</li>
          <li>• Empty arrays are acceptable for list fields (tags, prerequisites, etc.)</li>
          <li>• Existing features with the same feature_id will be updated</li>
        </ul>
      </div>
    </div>
  );
};

export default GrammarImport; 